import { trimStr } from './cfoFormat'

/**
 * Customer-facing copy for the typed refusals the CFO refresh routes answer.
 *
 * The server's refusal text is an OPERATOR contract: every typed refusal is
 * `"<code>: <detail>"` (`QboError`'s `Display`), and parked tasks, the pipeline
 * health census and the log greps all match on that prefix, so it must not
 * change. What must change is what a client READS: a person clicking "Refresh
 * QuickBooks" or "Update reports" was shown the operator string verbatim —
 * "Bigquery_quota_exhausted: …", "API 409: impact_bootstrap_required: …" and a
 * layout version UUID — none of which they can act on.
 *
 * So the codes stay where machines read them (the JSON `error` field, the HTTP
 * status, the logs) and this module owns the sentence a person reads. An
 * unrecognised refusal still shows its server text: an actual explanation is
 * worth reading. Only the machine prefix is removed from it, because by the
 * codebase's own convention that prefix is the code, which already travels
 * beside the message.
 */

/** `<code>: ` at the start of a typed refusal body. Codes are snake_case with
 * no spaces, so an ordinary English sentence containing a colon never matches. */
const CODE_PREFIX = /^([a-z][a-z0-9_]{2,}):\s+/
/** `QboError::Display` for the untyped arms, which reach `(StatusCode, String)`
 * handlers verbatim. "transient QBO error: …" is not an explanation. */
const QBO_DISPLAY_PREFIX = /^(?:transient|fatal) QBO error:\s+/i
/** `Error("API <status>: <body>")` built by `ServerApiClient.buildApiError`. */
const API_PREFIX = /^API (\d{3}):\s*/
/** The RFC3339 instant `QboError::BigQueryQuotaExhausted` names. */
const RESET_AT = /\b(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2}))/

export type CfoRefusal = {
  /** HTTP status when the failure came from an API answer, else null. */
  status: number | null
  /** The server's machine-readable refusal code, for branching — never shown. */
  code: string
  /** What a person reads: no codes, no UUIDs, no BigQuery job ids. */
  sentence: string
  /**
   * True only when this module RECOGNISED the refusal — a code in
   * `REFUSAL_COPY` or a `REFUSAL_PATTERNS` hit — so `sentence` is copy this
   * module wrote. False for everything else, including a transport rejection
   * (`Failed to fetch`) or an infrastructure message (`pool timed out …`),
   * where `sentence` is only the server's own text tidied up and a caller with
   * better copy for that case must prefer its own. Without this flag a caller
   * cannot tell the two apart: `sentence` is non-empty either way, so a
   * `sentence || fallback` chain silently shows browser and database internals.
   */
  matched: boolean
}

const toSentence = (text: string): string => {
  const trimmed = trimStr(text)
  if (!trimmed) return ''
  const capitalized = `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`
}

/** The moment the daily data-processing allowance resets, in the reader's own
 * time zone. `retryAfterMs` is the JSON body's `retry_after_ms`; the plain-text
 * body names the instant instead.
 *
 * The zone is named because the allowance resets at midnight Pacific while the
 * reader's device is on its own zone, and an accountant commonly reads it over
 * a client's shoulder on a third: an unlabelled "1:00 AM" is the only
 * actionable fact in the sentence and cannot be resolved without it. */
const resetWording = (detail: string, retryAfterMs?: number | null): string => {
  const named = detail.match(RESET_AT)?.[1]
  const at = named
    ? new Date(named)
    : typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs) && retryAfterMs > 0
      ? new Date(Date.now() + retryAfterMs)
      : null
  if (!at || Number.isNaN(at.getTime())) return 'Try again once the daily limit resets.'
  // Spelled out component by component, NOT as `dateStyle`/`timeStyle`:
  // ECMA-402 throws `TypeError: Invalid option : option` when either style is
  // combined with `timeZoneName`, and this formats the sentence that explains
  // a failure, so a throw here would blank the surface that was reporting it.
  // These components render what `medium`/`short` render, plus the zone.
  const when = at.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  return `Try again after ${when}.`
}

/**
 * One sentence per typed refusal. Each says what is happening and what happens
 * next, and none of them promises something the server does not do: an
 * explicit refresh refused on the quota records no request (it is refused
 * before `enqueue_for_period`), so the copy says the refresh has not started
 * rather than that it resumes by itself.
 */
const REFUSAL_COPY: Record<string, (detail: string, retryAfterMs?: number | null) => string> = {
  bigquery_quota_exhausted: (detail, retryAfterMs) =>
    `FuseGrid has reached today's data-processing limit, so this refresh has not started. ${resetWording(detail, retryAfterMs)}`,
  // Only FuseGrid can clear this one: the index is built by an operator
  // command (`provider_quickbooks::transaction_index::impact::
  // impact_bootstrap_required_reason` names it) and nothing is alerted when a
  // company is refused — `impact_bootstrap_required_error` only logs. So the
  // copy neither promises that someone was told nor sends the reader to a
  // screen that cannot fix it; the one step that moves this is telling us.
  impact_bootstrap_required: () =>
    'This company needs a one-time historical setup step from FuseGrid before its reports can refresh. It does not start on its own — contact FuseGrid to have it completed.',
  // Naming who acts, not just where: this button has no role gate, so a client
  // viewer with no access to the layout editor reads it too. Matches the
  // blocked banner's "Your accountant can resolve this" and keeps the editor's
  // name, which the binding-setup message already uses
  // (`cfo_binding_provisioning.rs`), for the reader who does have access.
  cfo_layout_unpublished: () =>
    "This report's statement layout has not been published yet, so reports cannot refresh. Your accountant can publish it in the statement layout editor.",
  cfo_layout_missing_from_projection: () =>
    "This report's statement layout was never sent for publishing, so reports cannot refresh. Your accountant can republish it in the statement layout editor.",
  // Intuit refused the company for the QuickBooks user who connected it. Its
  // operator text carries the Intuit fault code and transaction id.
  provider_authorization_denied: () =>
    'QuickBooks is no longer authorizing FuseGrid for this company. Reconnect it with a QuickBooks user who has access.',
}

/**
 * Refusals whose text has no code prefix to key on. Each carries an operator
 * identifier (a layout version UUID, a BigQuery job id, a workbook UUID) that a
 * person cannot use, so the whole sentence is replaced rather than trimmed.
 *
 * Matching on the server's prose makes that wording load-bearing for what a
 * client reads, and prose has no compiler: a reword would silently put the
 * identifier back in front of a customer with every test still green. So each
 * entry names the Rust source that formats it and the literal phrase it keys
 * on, and `cfoRefusalCopy.test.ts` reads those files and fails when the phrase
 * is no longer there. Give one of these a typed code on the server and it
 * belongs in `REFUSAL_COPY` instead, where the code is already a tested
 * contract.
 */
export type CfoRefusalPattern = {
  /** Repo-relative Rust source that formats this refusal. */
  source: string
  /** Literal substring of that format string, matched case-insensitively. */
  phrase: string
  sentence: string
}

export const REFUSAL_PATTERNS: CfoRefusalPattern[] = [
  {
    source: 'server/src/integrations/quickbooks/export.rs',
    phrase: 'is not yet published to the warehouse',
    sentence:
      "This report's statement layout is still being published. Reports can refresh as soon as that finishes.",
  },
  {
    source: 'server/src/integrations/quickbooks/export.rs',
    phrase: 'has no recorded outcome',
    sentence:
      'FuseGrid is still confirming the last statement-layout publish. Reports can refresh as soon as that finishes.',
  },
  {
    // `enqueue_with_coverage`'s per-workbook 428, which names the workbook's
    // UUID ("Company refresh target <uuid> is blocked …") on the same "Update
    // reports" button as the two refusals above.
    source: 'server/src/integrations/quickbooks/ml_forecasting/durable_materialization.rs',
    phrase: 'is blocked and the shared graph remains assembling',
    sentence:
      "FuseGrid is still assembling this company's reporting workflow, so this report's update has not started. Try again in a few minutes.",
  },
]

const escapeForRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** The refusal body: JSON from `qbo_error_response`, plain text from a
 * `(StatusCode, String)` handler. */
const refusalBody = (body: string): { code: string; detail: string; retryAfterMs: number | null } => {
  try {
    const parsed = JSON.parse(body)
    if (parsed && typeof parsed === 'object') {
      const retry = Number((parsed as any).retry_after_ms)
      return {
        code: trimStr((parsed as any).error),
        detail: trimStr((parsed as any).message),
        retryAfterMs: Number.isFinite(retry) ? retry : null,
      }
    }
  } catch {
    // Plain text, handled below.
  }
  return { code: trimStr(body.match(CODE_PREFIX)?.[1]), detail: body, retryAfterMs: null }
}

/**
 * Read one refusal — an `Error` from `ServerApiClient`, its message, or a raw
 * body — into the status and code machines branch on and the sentence a person
 * reads.
 */
export function parseCfoRefusal(error: unknown): CfoRefusal {
  const err = error as any
  const fromMessage = trimStr(err?.message ?? error)
  const apiStatus = Number(fromMessage.match(API_PREFIX)?.[1])
  const status = Number.isFinite(apiStatus)
    ? apiStatus
    : Number.isFinite(Number(err?.status))
      ? Number(err?.status)
      : null
  const body = trimStr(err?.body) || fromMessage.replace(API_PREFIX, '')
  const { code, detail, retryAfterMs } = refusalBody(body)
  // Own properties only: a `code` of "constructor" or "toString" would
  // otherwise resolve to an inherited function and throw while rendering an
  // error, blanking the surface that was trying to explain the failure.
  const copy = Object.prototype.hasOwnProperty.call(REFUSAL_COPY, code) ? REFUSAL_COPY[code] : null
  if (copy) return { status, code, sentence: copy(detail || body, retryAfterMs), matched: true }
  const plain = trimStr(detail).replace(QBO_DISPLAY_PREFIX, '')
  for (const { phrase, sentence } of REFUSAL_PATTERNS) {
    if (new RegExp(escapeForRegExp(phrase), 'i').test(plain)) {
      return { status, code, sentence, matched: true }
    }
  }
  // Unrecognised. A JSON refusal with a code and no message becomes the code's
  // own words, so the reader gets English ("Workbook not found.") and never
  // `workbook_not_found` — but `matched` stays false either way, because this
  // sentence is the server's text tidied up, not copy written for a reader.
  const readable = plain || (code ? code.replace(/_/g, ' ') : body)
  return { status, code, sentence: toSentence(readable.replace(CODE_PREFIX, '')), matched: false }
}

/** The sentence alone, for a surface that renders the refusal as its own text. */
export function cfoRefusalSentence(error: unknown): string {
  return parseCfoRefusal(error).sentence
}
